/**
 * Session persistence for the authenticated user object (the auth slice's
 * `currentUser`). Paired with tokenStore's sessionStorage-backed access token:
 * together they let a page reload restore a live session WITHOUT calling
 * `/v1/refresh`. That refresh, previously fired on every reload, raced against
 * the backend's refresh-token rotation and logged the user out on a rapid
 * double-refresh (the in-memory token + user were wiped each reload, forcing a
 * round-trip to re-mint them).
 *
 * We use sessionStorage (not localStorage) so the cached identity is scoped to
 * the tab session and cleared on tab close — mirroring tokenStore's access-token
 * policy. The user object carries roles/permissions but never a secret token
 * (those live only in tokenStore). The eslint localStorage boundary doesn't
 * apply to sessionStorage, but we still go through this single helper so the
 * persistence policy stays in one place.
 *
 * Failure modes: sessionStorage can throw in private-browsing / SSR /
 * quota-exceeded scenarios. Every call is wrapped in try/catch and returns a
 * safe fallback so the slice never crashes app boot.
 */

import type { User } from './types';

const SESSION_USER_KEY = 'om.sessionUser';

/**
 * Validate a parsed payload before trusting it as a User. A corrupt /
 * hand-edited entry must not flow into the auth slice. `id` + `email` are the
 * only fields `User` requires; that's enough of a shape check to reject junk.
 */
function isValidUser(value: unknown): value is User {
  if (!value || typeof value !== 'object') return false;
  const u = value as Record<string, unknown>;
  return typeof u.id === 'string' && typeof u.email === 'string';
}

export function readSessionUser(): User | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_USER_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidUser(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeSessionUser(user: User | null): void {
  try {
    if (user === null) {
      window.sessionStorage.removeItem(SESSION_USER_KEY);
      return;
    }
    window.sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
  } catch {
    // Best-effort — we don't surface a UI error if persistence fails.
  }
}
